/* =============================================================================
   HAKI native cart — data store (Shopify AJAX, optimistic).
   -----------------------------------------------------------------------------
   The single source of truth for the native cart drawer. Renders from a LOCAL
   model that mutates instantly on user actions (so +/- and the subtotal never
   wait on the network); the Shopify AJAX call fires in the background and the
   authoritative server cart reconciles the model when it lands.

   Responsiveness model:
     • changeLine/remove/add mutate local state + notify subscribers SYNCHRONOUSLY
     • network writes are COALESCED per line (spamming +/- sends only the latest
       quantity) and SERIALIZED (one in-flight write at a time)
     • after each write we re-read /cart.js and reconcile (true discounts, gift
       lines, inventory clamping) — then notify again

   Public API (window.HakiCart):
     subscribe(fn) -> unsubscribe     fn(state) on every change; called immediately
     getState()                       current cart model
     refresh()                        force re-read from /cart.js
     add(id, qty, props)              add a variant (returns Promise)
     changeLine(key, qty)             set a line's quantity (0 removes)
     remove(key)                      remove a line
     updateAttributes(attrs)          merge cart attributes via the SAME queue (Promise)
     subtotalCents()                  Σ line final_line_price (gift = 0)
     recommendations                  pluggable provider — see bottom

   Money is in cents throughout (Shopify convention).
   ============================================================================= */
(function () {
  'use strict';

  var EMPTY = { items: [], item_count: 0, total_price: 0, items_subtotal_price: 0, currency: 'USD' };
  var state = EMPTY;
  var subs = [];

  // --- network ---------------------------------------------------------------
  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
      cache: 'no-store'
    }).then(readJSON);
  }
  function getJSON(url) {
    return fetch(url, { headers: { Accept: 'application/json' }, cache: 'no-store' }).then(readJSON);
  }
  function readJSON(r) {
    return r.text().then(function (t) {
      var data; try { data = JSON.parse(t); } catch (e) { data = null; }
      if (!r.ok) {
        var err = new Error((data && (data.description || data.message)) || ('cart HTTP ' + r.status));
        err.status = r.status; err.data = data; throw err;
      }
      return data;
    });
  }

  // --- subscribers -----------------------------------------------------------
  // True while any write is queued or in flight (the cart hasn't settled). The UI
  // uses this to disable qty steppers so a shopper can't fire overlapping changes.
  function isBusy() {
    return inflight || !!flushTimer || Object.keys(pending).length > 0 || pendingAdds.length > 0 || !!pendingAttrs;
  }
  function notify() {
    if (state) state.busy = isBusy();
    for (var i = 0; i < subs.length; i++) { try { subs[i](state); } catch (e) { /* keep others */ } }
    // Also emit a DOM event so non-JS-store consumers (header badge etc.) react.
    try { document.dispatchEvent(new CustomEvent('haki-cart:change', { detail: state })); } catch (e) {}
  }
  function setState(cart) {
    if (!cart || !cart.items) return;
    state = cart;
    notify();
  }
  function subscribe(fn) {
    subs.push(fn);
    if (state && state.items) { try { fn(state); } catch (e) {} }
    return function () { subs = subs.filter(function (x) { return x !== fn; }); };
  }

  // --- derived ---------------------------------------------------------------
  function lineFinal(i) {
    return (i.final_line_price != null) ? i.final_line_price
      : (i.line_price != null ? i.line_price : (i.price || 0) * (i.quantity || 0));
  }
  function subtotalCents() {
    return (state.items || []).reduce(function (s, i) { return s + lineFinal(i); }, 0);
  }
  function itemCount() {
    return (state.items || []).reduce(function (s, i) { return s + (i.quantity || 0); }, 0);
  }
  function recomputeLocal() {
    // Optimistic line totals from unit price × qty (server reconciles discounts).
    (state.items || []).forEach(function (i) {
      i.final_line_price = (i.price || 0) * (i.quantity || 0);
      i.line_price = i.original_line_price = i.final_line_price;
    });
    state.item_count = itemCount();
    state.items_subtotal_price = subtotalCents();
    state.total_price = state.items_subtotal_price;
  }

  // --- write queue (coalesced per line, serialized) --------------------------
  var pending = {};       // key -> desired quantity (latest intent wins)
  var inflight = false;
  var pendingAdds = [];   // [{id, quantity, properties, resolve, reject}]
  var flushTimer = null;
  var pendingAttrs = null;  // merged cart-attribute patch (latest value per key wins)
  var attrWaiters = [];     // [{resolve, reject}] for updateAttributes() callers

  // Trailing, non-resetting debounce: a burst of +/- within the window collapses
  // to a single change request carrying the LATEST quantity. Adds flush now.
  function scheduleFlush(immediate) {
    if (immediate) { clearTimeout(flushTimer); flushTimer = null; flush(); return; }
    if (flushTimer || inflight) return;
    flushTimer = setTimeout(function () { flushTimer = null; flush(); }, 250);
  }

  // POST EVERY queued quantity change to the server, sequentially, clearing the
  // queue as it goes. Resolves to the LAST change's cart (the settled truth), or
  // undefined if nothing was queued. Sends every change before resolving — and
  // critically does NOT setState between individual changes — so no stale,
  // half-reconciled intermediate cart is ever rendered. Two cases this guards:
  //   • forward: before an add's /cart.js refetch, so the refetch isn't stale.
  //   • reverse: when a qty reduction drops the cart below the gift threshold,
  //     reconcileGifts queues the gift REMOVAL reentrantly (during the qty
  //     change's optimistic notify), so the gift key lands in `pending` BEFORE
  //     the qty key. Sending the gift removal first — while the server still has
  //     the OLD higher quantity — returns a cart at the OLD subtotal, which made
  //     the reward bar bounce UP then back down. Draining all-then-render fixes it.
  function drainPendingChanges() {
    var keys = Object.keys(pending);
    if (!keys.length) return Promise.resolve();
    return keys.reduce(function (chain, key) {
      var qty = pending[key];        // latest intent for this line at send time
      delete pending[key];
      return chain.then(function () { return postJSON('/cart/change.js', { id: key, quantity: qty }); });
    }, Promise.resolve());
  }

  // POST the merged attribute patch, if any, as ONE /cart/update.js inside the
  // serialized flush. Before this existed the A/B attributes went out as a raw
  // fetch at the same moment as the free-gift /cart/add.js, and two overlapping
  // cart writes can lose one of them (order #HS120011: attributes, no gift, while
  // the drawer believed the gift was in the bag). Never throws: an attribute
  // failure must not reject a gift add that shares the flush. Resolves to the
  // cart Shopify returned, or undefined if nothing was queued / it failed.
  function drainPendingAttrs() {
    if (!pendingAttrs) return Promise.resolve();
    var attrs = pendingAttrs, waiters = attrWaiters;
    pendingAttrs = null; attrWaiters = [];
    return postJSON('/cart/update.js', { attributes: attrs }).then(function (cart) {
      waiters.forEach(function (w) { w.resolve(cart); });
      return cart;
    }, function (e) {
      waiters.forEach(function (w) { w.reject(e); });
      return undefined;
    });
  }
  function moreQueued() {
    return Object.keys(pending).length || pendingAdds.length || !!pendingAttrs;
  }

  function flush() {
    if (inflight) return;
    // Adds first (so a newly added line can then be adjusted).
    if (pendingAdds.length) {
      var a = pendingAdds.shift();
      inflight = true;
      postJSON('/cart/add.js', { id: a.id, quantity: a.quantity, properties: a.properties || {} })
        .then(function () { return drainPendingChanges(); })  // settle qty edits BEFORE the refetch so /cart.js isn't stale
        .then(function () { return drainPendingAttrs(); })    // ...and attribute writes, same reason
        .then(function () { return getJSON('/cart.js'); })
        .then(function (cart) { setState(cart); a.resolve && a.resolve(cart); })
        .catch(function (e) { a.reject && a.reject(e); return getJSON('/cart.js').then(setState).catch(function () {}); })
        .then(function () {
        inflight = false;
        // More queued work? keep draining (busy stays true). Otherwise emit a
        // settle notify so the UI learns busy=false and re-enables the steppers.
        if (moreQueued()) scheduleFlush(true);
        else notify();
      });
      return;
    }
    if (!Object.keys(pending).length && !pendingAttrs) return;
    inflight = true;
    drainPendingChanges()                          // send ALL queued changes...
      .then(function (cart) { return drainPendingAttrs().then(function (c) { return c || cart; }); })  // ...then any attribute patch
      .then(function (cart) { if (cart) setState(cart); })  // ...then render ONCE with the settled cart
      .catch(function () { return getJSON('/cart.js').then(setState).catch(function () {}); })
      .then(function () {
        inflight = false;
        // More queued work? keep draining (busy stays true). Otherwise emit a
        // settle notify so the UI learns busy=false and re-enables the steppers.
        if (moreQueued()) scheduleFlush(true);
        else notify();
      });
  }

  // --- mutations (optimistic) ------------------------------------------------
  function changeLine(key, qty) {
    qty = Math.max(0, qty | 0);
    // Queue the shopper's intended line change BEFORE notifying subscribers.
    // reconcileGifts() can enqueue a dependent gift removal during that notify;
    // by seeding the paid-line change first, the server sees the subtotal drop
    // before the gift removal and won't "heal" the gift back into the cart.
    pending[key] = qty;         // background reconcile
    var idx = (state.items || []).findIndex(function (i) { return i.key === key; });
    if (idx !== -1) {
      if (qty === 0) {
        state.items.splice(idx, 1);
      } else {
        // Optimistic: recompute ONLY this line's total from unit price. Other
        // lines keep their server values — critically the $0 gift line, which a
        // generic recompute would wrongly re-price from its unit cost. (User-
        // editable lines are never discounted; the server reconciles anything.)
        var it = state.items[idx];
        it.quantity = qty;
        it.final_line_price = it.line_price = it.original_line_price = (it.price || 0) * qty;
      }
      state.item_count = itemCount();
      state.items_subtotal_price = subtotalCents();
      state.total_price = state.items_subtotal_price;
      notify();                 // INSTANT render
    }
    scheduleFlush(false);       // coalesce bursts
  }
  function remove(key) { changeLine(key, 0); }

  function add(id, qty, properties) {
    qty = qty || 1;
    return new Promise(function (resolve, reject) {
      pendingAdds.push({ id: id, quantity: qty, properties: properties, resolve: resolve, reject: reject });
      notify();                 // publish busy=true now (an add is queued) so the UI stays locked
      scheduleFlush(true);      // adds flush immediately
    });
  }

  // Merge cart attributes through the write queue, so they can never overlap a
  // gift add or a quantity change. Safe to call from inside a subscriber: it does
  // NOT notify (no re-entrant render); it marks the current state busy so the
  // caller's "settled?" check (reconcileGifts) waits for the settle notify that
  // follows the write, then re-runs against the real server cart.
  function updateAttributes(attrs) {
    return new Promise(function (resolve, reject) {
      pendingAttrs = Object.assign(pendingAttrs || {}, attrs || {});
      attrWaiters.push({ resolve: resolve, reject: reject });
      if (state) state.busy = true;
      scheduleFlush(true);
    });
  }

  function refresh() { return getJSON('/cart.js').then(setState); }

  // --- recommendations: pluggable provider -----------------------------------
  // Default = Shopify's native related-products endpoint. Swap the provider to
  // change engines (e.g. a future Rebuy-style recommender) WITHOUT touching the
  // drawer: HakiCart.recommendations.setProvider(fn) where
  //   fn({ productId, limit }) -> Promise<Array<product>>
  function shopifyProvider(opts) {
    var limit = (opts && opts.limit) || 8;
    var pid = opts && opts.productId;
    // Related-products needs a seed product. With one (non-empty cart) use the
    // recommendations engine; with none (EMPTY cart) Shopify returns nothing, so
    // fall back to a product list so the suggestions still populate.
    if (pid) {
      return getJSON('/recommendations/products.json?intent=related&limit=' + limit + '&product_id=' + pid)
        .then(function (j) { return (j && j.products) || []; })
        .then(function (list) { return list.length ? list : fallbackProducts(limit); })
        .catch(function () { return fallbackProducts(limit); });
    }
    return fallbackProducts(limit);
  }
  function fallbackProducts(limit) {
    // /products.json reports prices as DOLLAR STRINGS ("45.00") and images as
    // objects — normalize to the recommendations shape (cents + featured_image
    // string) so the drawer renders them identically.
    return getJSON('/products.json?limit=' + limit)
      .then(function (j) { return ((j && j.products) || []).map(normalizeProduct); })
      .catch(function () { return []; });
  }
  function toCents(p) { return (typeof p === 'number') ? p : Math.round(parseFloat(p) * 100) || 0; }
  function normalizeProduct(p) {
    var img = p.featured_image;
    if (!img && p.images && p.images.length) img = (typeof p.images[0] === 'string') ? p.images[0] : p.images[0].src;
    var variants = (p.variants || []).map(function (v) {
      return { id: v.id, title: v.title, price: toCents(v.price), available: v.available !== false };
    });
    return {
      id: p.id, title: p.title, handle: p.handle, url: '/products/' + p.handle,
      featured_image: img,
      available: variants.some(function (v) { return v.available; }),
      price: variants[0] ? variants[0].price : 0,
      variants: variants
    };
  }
  var recommendations = {
    _provider: shopifyProvider,
    setProvider: function (fn) { if (typeof fn === 'function') this._provider = fn; },
    get: function (opts) { try { return Promise.resolve(this._provider(opts || {})); } catch (e) { return Promise.resolve([]); } }
  };

  // --- expose ----------------------------------------------------------------
  window.HakiCart = {
    subscribe: subscribe,
    getState: function () { return state; },
    refresh: refresh,
    add: add,
    changeLine: changeLine,
    remove: remove,
    updateAttributes: updateAttributes,
    subtotalCents: subtotalCents,
    itemCount: itemCount,
    recommendations: recommendations,
    // test/diagnostic hooks
    _setState: setState,
    _recomputeLocal: recomputeLocal
  };

  // Prime from the server cart on load.
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh);
  else refresh();
})();
